/**
 * GoldenWings Landing Page
 * Main entry point for flight school management
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography } from 'antd';
import {
  CalendarOutlined,
  SendOutlined,
  EnvironmentOutlined,
  SolutionOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import './GoldenWingsPage.css';

const { Title, Paragraph } = Typography;

interface ModuleCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  description,
  icon,
  onClick,
}) => {
  return (
    <Card
      hoverable
      className="goldenwings-module-card"
      onClick={onClick}
      style={{ height: '100%' }}
    >
      <div className="module-card-icon">{icon}</div>
      <Title level={4}>{title}</Title>
      <Paragraph type="secondary">{description}</Paragraph>
    </Card>
  );
};

export const GoldenWingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const modules = [
    {
      title: t('goldenwings.calendar'),
      description: t('goldenwings.calendar_description'),
      icon: <CalendarOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
      path: '/apps/goldenwings/calendar',
    },
    {
      title: t('goldenwings.airplanes'),
      description: t('goldenwings.airplanes_description'),
      icon: <SendOutlined style={{ fontSize: 48, color: '#52c41a' }} />,
      path: '/apps/goldenwings/resources/airplanes',
    },
    {
      title: t('goldenwings.airports'),
      description: t('goldenwings.airports_description'),
      icon: <EnvironmentOutlined style={{ fontSize: 48, color: '#faad14' }} />,
      path: '/apps/goldenwings/resources/airports',
    },
    {
      title: t('goldenwings.instructors'),
      description: t('goldenwings.instructors_description'),
      icon: <SolutionOutlined style={{ fontSize: 48, color: '#722ed1' }} />,
      path: '/apps/goldenwings/resources/instructors',
    },
    {
      title: t('goldenwings.students'),
      description: t('goldenwings.students_description'),
      icon: <TeamOutlined style={{ fontSize: 48, color: '#eb2f96' }} />,
      path: '/apps/goldenwings/resources/students',
    },
  ];

  return (
    <div className="goldenwings-page">
      <div className="goldenwings-header">
        <Title level={2}>{t('goldenwings.title')}</Title>
        <Paragraph>{t('goldenwings.subtitle')}</Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        {modules.map((module) => (
          <Col xs={24} sm={12} lg={8} key={module.path}>
            <ModuleCard
              title={module.title}
              description={module.description}
              icon={module.icon}
              onClick={() => navigate(module.path)}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};
